import { useEffect } from "react";
import { Link } from "@remix-run/react";
import { ArrowRight, Flag, Globe, MapPin, LucideIcon } from "lucide-react";
import { Card } from "~/components/ui/card";
import { useRegionConfig } from "~/store/regionConfigStore";
import { useNavStore } from "~/store/navStore";
import {
  getRegionUrlPrefix,
  getRegionUrlPrefixWithCountryCode,
} from "~/lib/helpers/slugHelper";
import {
  NAV_DROPDOWN_CARD_CLASS,
  NAV_DROPDOWN_ERROR_CARD_CLASS,
  NAV_DROPDOWN_MAIN_PANEL_CLASS,
  NAV_DROPDOWN_PROMO_PANEL_CLASS,
} from "../constant";
import {
  buildPropertyNavCityUrl,
  buildPropertyNavCountryUrl,
  buildPropertyNavStateUrl,
} from "~/components/blocks/Geosearch/locationGeosearch";

type FindLocationProps = {
  /** Opens UniqueVisitorModal from Header so it survives the nav dropdown unmounting. */
  onEnquire?: () => void;
};

const columnLabelClass =
  "text-xs font-semibold uppercase tracking-[0.08em] text-oh-grey-heavy";

const itemClass =
  "flex h-8 items-center gap-2 rounded-md px-2 py-2 text-sm text-oh-black transition-colors hover:bg-gray-100";

const viewAllClass =
  "flex h-8 items-center gap-1.5 px-1 text-base font-semibold tracking-[0.08em] text-oh-black";

const FindLocation = ({ onEnquire }: FindLocationProps = {}) => {
  const { regionConfig } = useRegionConfig();
  const { locationData, locationLoading, locationError, fetchLocationData } =
    useNavStore();

  useEffect(() => {
    fetchLocationData(regionConfig.country);
  }, [fetchLocationData, regionConfig.country]);

  if (locationError) {
    return (
      <Card className={NAV_DROPDOWN_ERROR_CARD_CLASS}>
        <div className='text-center text-red-500'>{locationError}</div>
      </Card>
    );
  }

  if (locationLoading) {
    return (
      <Card className={NAV_DROPDOWN_CARD_CLASS}>
        <div className={`${NAV_DROPDOWN_MAIN_PANEL_CLASS} p-6 lg:p-8`}>
          <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
            {[...Array(3)].map((_, col) => (
              <div key={col} className='w-full min-w-0 space-y-4'>
                <div className='h-4 w-28 animate-pulse rounded bg-gray-200' />
                <div className='space-y-2'>
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className='h-7 animate-pulse rounded bg-gray-200'
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={NAV_DROPDOWN_PROMO_PANEL_CLASS}>
          <div className='h-4 w-28 animate-pulse rounded bg-gray-200' />
          <div className='mt-2 h-6 w-full animate-pulse rounded bg-gray-200' />
          <div className='mt-4 h-44 w-full animate-pulse rounded-lg bg-gray-200' />
          <div className='mt-4 space-y-2'>
            <div className='h-3 w-full animate-pulse rounded bg-gray-200' />
            <div className='h-3 w-4/5 animate-pulse rounded bg-gray-200' />
          </div>
          <div className='mt-5 h-12 w-full animate-pulse rounded-md bg-gray-200' />
        </div>
      </Card>
    );
  }

  const ColumnIcon = ({ icon: Icon }: { icon: LucideIcon }) => (
    <Icon size={18} strokeWidth={1.5} className='shrink-0 text-oh-grey-heavy' />
  );

  const regionPrefix = getRegionUrlPrefixWithCountryCode(regionConfig.region);
  const popularCities = locationData?.topStatesWithCountry?.slice(0, 8) ?? [];
  const topRegions = locationData?.topStates?.slice(0, 6) ?? [];
  const worldwideCountries = [
    ...new Set(
      locationData?.worldWideTopStates
        ?.map((city) => city.country)
        .filter(Boolean) ?? [],
    ),
  ].slice(0, 6);

  const locationColumnCount = [
    popularCities.length > 0,
    topRegions.length > 0,
    worldwideCountries.length > 0,
  ].filter(Boolean).length;

  const locationGridClass =
    locationColumnCount <= 1
      ? "grid-cols-1"
      : locationColumnCount === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <Card className={NAV_DROPDOWN_CARD_CLASS}>
      <div className={`${NAV_DROPDOWN_MAIN_PANEL_CLASS} p-6 pb-1 lg:p-8 lg:pb-2`}>
        <div className={`grid gap-8 ${locationGridClass}`}>
          {popularCities.length > 0 && (
            <div className='w-full min-w-0 lg:w-[190px]'>
              <span className={columnLabelClass}>Popular Cities</span>
              <div className='mt-4 flex flex-col gap-1'>
                {popularCities.map((city, i) => (
                  <Link
                    key={city.suburb + i}
                    data-spinner='true'
                    to={buildPropertyNavCityUrl(
                      getRegionUrlPrefix(city?.country),
                      city.state ?? "",
                      city.suburb ?? "",
                    )}
                    prefetch='intent'
                    className={itemClass}
                  >
                    <ColumnIcon icon={MapPin} />
                    <span className='truncate'>{city.suburb}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {topRegions.length > 0 && (
            <div className='w-full min-w-0 lg:w-[190px]'>
              <span className={columnLabelClass}>Top Regions</span>
              <div className='mt-4 flex flex-col gap-1'>
                {topRegions.map((state, i) => (
                  <Link
                    key={state.state + i}
                    data-spinner='true'
                    to={buildPropertyNavStateUrl(
                      getRegionUrlPrefixWithCountryCode(
                        state.country_iso_code || regionConfig.region,
                      ),
                      state.state,
                    )}
                    prefetch='intent'
                    className={itemClass}
                  >
                    <ColumnIcon icon={Flag} />
                    <span className='truncate'>{state.state}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {worldwideCountries.length > 0 && (
            <div className='w-full min-w-0 lg:w-[190px]'>
              <span className={columnLabelClass}>Worldwide</span>
              <div className='mt-4 flex flex-col gap-1'>
                {worldwideCountries.map((country, i) => (
                  <Link
                    key={country + i}
                    data-spinner='true'
                    to={buildPropertyNavCountryUrl(getRegionUrlPrefix(country))}
                    prefetch='intent'
                    className={itemClass}
                    title={country}
                  >
                    <ColumnIcon icon={Globe} />
                    <span className='truncate'>{country}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className='mt-auto flex justify-end border-t border-gray-200 pt-4 pb-2'>
          <Link
            data-spinner='true'
            to={buildPropertyNavCountryUrl(regionPrefix)}
            prefetch='intent'
            className={viewAllClass}
          >
            View All
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <div className={NAV_DROPDOWN_PROMO_PANEL_CLASS}>
        <span className='text-xs font-semibold uppercase tracking-[0.08em] text-oh-primary'>
          Find Office
        </span>

        <span className='mt-2 text-lg font-bold leading-7 text-black'>
          180+ Cities Worldwide
        </span>
        <img
          src={`https://ik.imagekit.io/officehub/tr:h-600,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/locations_header.avif`}
          alt='Modern flexible office space'
          className='mt-4 h-44 w-full rounded-lg object-cover'
          loading='lazy'
        />

        <p className='mt-4 text-sm leading-7 text-oh-dark-gray'>
          Discover and book coworking spaces, serviced offices, and flexible workspaces across more than{" "}
          <span className='font-semibold text-black'>
            180+ cities,
          </span>{" "}
          all in just a few clicks.
        </p>

        <button
          type='button'
          className='mt-auto flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-oh-primary text-sm font-semibold text-white transition-colors hover:bg-oh-dullTeal'
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onEnquire?.();
          }}
        >
          Enquire Now
          <ArrowRight size={18} />
        </button>
      </div>
    </Card>
  );
};

export default FindLocation;
